// Modo "Shorts Studio" — el usuario sube su video (talking-head), elige una plantilla de
// estilo, y el backend (Gemini Omni) lo re-estiliza por completo (Camino B). Pantalla propia.
// Reusa el design system: CreditBadge (costo), OptionSheet (bottom sheet mobile), cards
// seleccionables con ring, subida directa a video-project-assets. Ver spec de Shorts Studio.
//
// UX: en DESKTOP, layout de 2 columnas (panel de video/generar a la izquierda + grilla a la
// derecha). En MOBILE, la grilla ocupa la pantalla y al tocar una plantilla SUBE un OptionSheet
// desde abajo con subir-video + costo + generar (patrón mobile de Atlas). Subir = TAP (sin
// drag-and-drop, que en mobile no sirve).
//
// NOTA: por ahora se monta en una ruta de preview (/shorts-studio) para iterar el diseño;
// la integración fina como modo V2 (modesCatalog + InterfazV2) va después.

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, Check, Loader2, Download, RefreshCw, Sparkles, ArrowLeft, Play, Maximize2, X } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { CreditBadge } from '../../../components/CreditBadge';
import OptionSheet from '../../../components/opciones/OptionSheet';
import { getShortsTemplate, shortsTemplatesVisibles } from '../../../lib/shortsTemplates';
import { useUserGroup } from '../../../hooks/useUserGroup';
import { generarShort, creditsForShort, type ShortsStatus } from '../../../lib/shortsStudio';
import { generarShortCompose } from '../../../lib/shortsCompose';
import { generarShortScene, creditsForShortScene } from '../../../lib/shortsScene';
import { descargarArchivo } from '../../../utils/descargarArchivo';

const BUCKET = 'video-project-assets';

/** Duración (segundos) de un File de video, midiendo su metadata en un <video> oculto. */
function medirDuracion(file: File): Promise<number> {
  return new Promise((resolve) => {
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.onloadedmetadata = () => {
      const d = v.duration;
      URL.revokeObjectURL(v.src);
      resolve(Number.isFinite(d) ? d : 0);
    };
    v.onerror = () => resolve(0);
    v.src = URL.createObjectURL(file);
  });
}

/** true en viewport mobile (<768px). Reacciona al resize. */
function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const handler = () => setIsMobile(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isMobile;
}

export default function ShortsStudioMode() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  // Gate del motor 'scene': sus plantillas (Radio/Podcast/Jet/Penthouse/Neon) solo se muestran a
  // dev_testers hasta abrir el motor a todos (SHORTS_SCENE_ENABLED). El resto ve las de siempre.
  const { isDevTester } = useUserGroup();
  const templatesVisibles = shortsTemplatesVisibles(isDevTester);
  const [companyId, setCompanyId] = useState<string | null>(null);

  const [templateId, setTemplateId] = useState<string | null>(null);
  const [conSubtitulos, setConSubtitulos] = useState(true); // toggles del modo composición (Pop!/Titular)
  const [conEmojis, setConEmojis] = useState(true);
  const [fullscreen, setFullscreen] = useState(false); // overlay de pantalla completa del resultado
  const [descargando, setDescargando] = useState(false);
  const [verPlantillas, setVerPlantillas] = useState(false); // al generar se ocultan las plantillas y se ve el resultado; este flag las trae de vuelta
  const [sheetOpen, setSheetOpen] = useState(false); // solo mobile

  const [localUrl, setLocalUrl] = useState<string | null>(null); // preview inmediato (objectURL)
  const [signedUrl, setSignedUrl] = useState<string | null>(null); // URL en Storage para el backend
  const [durationSec, setDurationSec] = useState(0);
  const [subiendo, setSubiendo] = useState(false);

  const [status, setStatus] = useState<ShortsStatus | null>(null);
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  // Contador de subidas: cada selección invalida a las anteriores en vuelo (evita que una subida vieja
  // que termina tarde pise el estado de la más reciente → generaríamos/cobraríamos el video equivocado).
  const uploadSeqRef = useRef(0);

  useEffect(() => {
    (async () => {
      const { data: auth } = await supabase.auth.getUser();
      if (!auth.user) return;
      const { data: p } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', auth.user.id)
        .maybeSingle();
      setCompanyId((p as { id_company?: string | null } | null)?.id_company ?? null);
    })();
  }, []);

  // Gate anti-carrera del rollout: `useUserGroup` devuelve primero el valor CACHEADO (que puede exponer
  // una plantilla 'scene') y recién después el fetch autoritativo. Si el usuario resulta NO dev_tester, la
  // card 'scene' desaparece pero `templateId` seguiría apuntándola. Tomamos como seleccionada SOLO una
  // plantilla que siga estando entre las visibles; si no, `template` queda undefined → `puedeGenerar` y el
  // guard de `generar` bloquean el job gateado. Cierra el bypass del gate por selección vieja.
  const templateSeleccionada = getShortsTemplate(templateId);
  const template =
    templateSeleccionada && templatesVisibles.some((t) => t.id === templateSeleccionada.id)
      ? templateSeleccionada
      : undefined;
  // El modo 'scene' (Omni + sync-3 + Topaz 4k) cuesta más por segundo que el resto (1 cr/s).
  const cost = template?.engine === 'scene' ? creditsForShortScene(durationSec) : creditsForShort(durationSec);
  const generando = status === 'processing';
  const puedeGenerar = !!template && !!signedUrl && !subiendo && !generando;

  const elegirArchivo = () => fileInputRef.current?.click();

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permitir re-subir el mismo archivo
    if (!file) return;
    if (!companyId) {
      setError('No pudimos identificar tu cuenta. Recargá la página.');
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      setError('El video supera los 100 MB. Probá con uno más liviano.');
      return;
    }
    // Esta subida pasa a ser la vigente; las anteriores en vuelo quedan invalidadas por su `seq`.
    const seq = ++uploadSeqRef.current;
    setError(null);
    setResultUrl(null);
    setSignedUrl(null); // el video anterior deja de valer apenas se elige otro: no se puede generar hasta que ESTE suba
    setLocalUrl(URL.createObjectURL(file));
    setDurationSec(await medirDuracion(file));
    if (seq !== uploadSeqRef.current) return; // llegó otra selección mientras medíamos la duración

    setSubiendo(true);
    try {
      const ext = (file.name.split('.').pop() || 'mp4').toLowerCase();
      const path = `${companyId}/shorts/${crypto.randomUUID()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from(BUCKET)
        .upload(path, file, { upsert: true, contentType: file.type || 'video/mp4' });
      if (upErr) throw upErr;
      // 3h: el job async (chunks en serie) puede tardar varios minutos.
      const { data, error: sErr } = await supabase.storage.from(BUCKET).createSignedUrl(path, 60 * 60 * 3);
      if (sErr || !data?.signedUrl) throw sErr || new Error('sin URL firmada');
      if (seq !== uploadSeqRef.current) return; // una selección más nueva ganó: no pisar su signedUrl
      setSignedUrl(data.signedUrl);
    } catch {
      if (seq !== uploadSeqRef.current) return; // el fallo es de una subida vieja: no tocar el estado nuevo
      setError('No se pudo subir el video. Probá de nuevo.');
      setLocalUrl(null);
      setSignedUrl(null); // que un fallo NUNCA deje un signedUrl viejo usable
    } finally {
      if (seq === uploadSeqRef.current) setSubiendo(false); // solo la subida vigente libera el spinner
    }
  };

  const generar = async () => {
    if (!puedeGenerar || !template || !signedUrl) return;
    setError(null);
    setResultUrl(null);
    setVerPlantillas(false); // ocultar las plantillas: el área derecha pasa a mostrar el resultado
    if (isMobile) setSheetOpen(false); // mobile: cerrar el sheet para ver el resultado en la grilla
    try {
      // Ruteo por motor de la plantilla:
      //  • 'compose' (ej Pop!) → entorno por composición (fondo Omni + persona recortada + subtítulos).
      //  • 'scene'   (ej Radio) → entorno "scene" (Omni cambia el fondo + sync-3 boca + Topaz 4k).
      //  • resto (omni)         → pipeline Omni original.
      let res;
      if (template.engine === 'compose') {
        res = await generarShortCompose(
          { projectId: crypto.randomUUID(), sourceUrl: signedUrl, template: template.id, aspectRatio: '9:16', durationSec, subtitles: conSubtitulos, emojis: conEmojis },
          setStatus,
        );
      } else if (template.engine === 'scene') {
        res = await generarShortScene(
          { projectId: crypto.randomUUID(), sourceUrl: signedUrl, templatePrompt: template.prompt, template: template.id, aspectRatio: '9:16', durationSec },
          setStatus,
        );
      } else {
        res = await generarShort(
          { projectId: crypto.randomUUID(), sourceUrl: signedUrl, templatePrompt: template.prompt, aspectRatio: '9:16', durationSec },
          setStatus,
        );
      }
      setResultUrl(res.signedUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo generar el short.');
    } finally {
      setStatus(null);
    }
  };

  const seleccionarPlantilla = (id: string) => {
    setTemplateId(id);
    if (isMobile) setSheetOpen(true); // mobile: sube el sheet desde abajo
  };

  const videoSrc = localUrl; // el panel izq muestra SOLO el input; el resultado va al área derecha

  const descargar = async () => {
    if (!resultUrl || descargando) return;
    setDescargando(true);
    try {
      await descargarArchivo({ url: resultUrl, fileName: `${(template?.nombre ?? 'short').toLowerCase().replace(/\s+/g, '-')}.mp4`, esVideo: true });
    } catch {
      /* el usuario canceló la hoja de compartir o falló: no bloquea */
    } finally {
      setDescargando(false);
    }
  };

  // Contenido común (video + subir + costo + resultado/error). El botón "Generar" difiere:
  // en desktop es propio (con el token), en mobile es el botón Aceptar del OptionSheet.
  const contenido = (
    <div className="flex flex-col gap-3">
      {videoSrc ? (
        <div>
          <div className="relative rounded-2xl overflow-hidden bg-black aspect-[9/16] max-h-[46vh] mx-auto">
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <video src={videoSrc} controls controlsList="nodownload noplaybackrate noremoteplayback" disablePictureInPicture playsInline className="w-full h-full object-contain" />
            {subiendo && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/40 text-white text-[13px] gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Subiendo…
              </div>
            )}
          </div>
          <button
            onClick={elegirArchivo}
            className="inline-flex items-center gap-1.5 mt-2.5 text-[13px] font-medium text-brand-primary hover:opacity-80"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Cambiar video
          </button>
        </div>
      ) : (
        <button
          onClick={elegirArchivo}
          className="w-full rounded-2xl border border-neutral-300 bg-surface-card hover:border-brand-primary/50 flex flex-col items-center justify-center gap-2 py-10 text-neutral-500 hover:text-brand-primary transition-colors"
        >
          <Upload className="w-7 h-7" />
          <span className="text-[14px] font-medium">Subir video</span>
          <span className="text-[12px] text-neutral-400">vertical · hasta 100 MB</span>
        </button>
      )}

      {/* Toggles del modo composición (Pop!/Titular). Clay (transformación) no los muestra: va limpio. */}
      {template && videoSrc && template.engine === 'compose' && template.tipo === 'entorno' && (
        <div className="flex flex-col gap-2.5 pt-1">
          {([
            { label: 'Subtítulos', on: conSubtitulos, set: setConSubtitulos },
            { label: 'Emojis', on: conEmojis, set: setConEmojis },
          ] as const).map(({ label, on, set }) => (
            <button
              key={label}
              type="button"
              onClick={() => set(!on)}
              className="flex items-center justify-between text-[13.5px] text-brand-black/80"
            >
              <span>{label}</span>
              <span className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${on ? 'bg-brand-primary' : 'bg-neutral-300'}`}>
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${on ? 'translate-x-[18px]' : 'translate-x-0.5'}`} />
              </span>
            </button>
          ))}
        </div>
      )}

      {/* En DESKTOP el botón Generar va en el aside; en MOBILE va acá, largo, debajo de los toggles. */}
      {isMobile && (
        <button
          onClick={generar}
          disabled={!puedeGenerar}
          className="w-full h-11 inline-flex items-center justify-center gap-2 rounded-lg bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-40 disabled:cursor-not-allowed text-white text-[15px] font-medium shadow-sm transition-colors"
        >
          {generando ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> Generando…
            </>
          ) : (
            <>
              Generar {cost > 0 && <CreditBadge credits={cost} tone="on-primary" size="sm" />}
            </>
          )}
        </button>
      )}
      {error && <div className="rounded-lg bg-error-soft text-error-text text-[12.5px] px-3 py-2">{error}</div>}
    </div>
  );

  // ¿Mostrar el RESULTADO en el área derecha (ocupando el lugar de las plantillas)?
  const mostrarResultado = (generando || !!resultUrl) && !verPlantillas;

  // Card del RESULTADO — MISMO tamaño que una card de plantilla (aspect-[3/4]), va en la 1ª posición.
  // Botoncitos estilo Higgsfield al hover (agrandar / descargar); click → pantalla completa.
  const resultadoCard = resultUrl ? (
    <div className="group relative aspect-[3/4] rounded-2xl overflow-hidden ring-1 ring-black/[0.06] bg-black">
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video src={`${resultUrl}#t=0.1`} muted playsInline preload="metadata" className="absolute inset-0 w-full h-full object-cover" />
      <button
        type="button"
        onClick={() => setFullscreen(true)}
        aria-label="Ver en grande"
        className="absolute inset-0 flex items-center justify-center bg-black/15 opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <span className="w-11 h-11 rounded-full bg-black/55 backdrop-blur-sm flex items-center justify-center">
          <Play className="w-5 h-5 text-white translate-x-[1px]" />
        </span>
      </button>
      <div className="absolute top-2 right-2 flex flex-col gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <button onClick={() => setFullscreen(true)} aria-label="Agrandar" className="w-8 h-8 rounded-full bg-black/55 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/75 transition-colors">
          <Maximize2 className="w-4 h-4" />
        </button>
        <button onClick={descargar} disabled={descargando} aria-label="Descargar" className="w-8 h-8 rounded-full bg-black/55 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/75 transition-colors disabled:opacity-60">
          {descargando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
        </button>
      </div>
      <span className="absolute bottom-2 left-2 text-[11px] font-medium bg-white/90 text-brand-black px-2 py-0.5 rounded-full">Resultado</span>
    </div>
  ) : (
    <div className="relative aspect-[3/4] rounded-2xl overflow-hidden ring-1 ring-black/[0.06] bg-neutral-100 flex flex-col items-center justify-center gap-2.5 animate-pulse">
      <div className="w-11 h-11 rounded-full bg-brand-primary/10 flex items-center justify-center">
        <Loader2 className="w-5 h-5 text-brand-primary animate-spin" />
      </div>
      <span className="text-[13px] font-medium text-neutral-500">Generando…</span>
      <span className="text-[11px] text-neutral-400 px-4 text-center">Tarda unos minutos</span>
    </div>
  );

  return (
    <div className="min-h-screen bg-surface-bg">
      {/* Mobile: menos padding arriba (antes pt-16 dejaba mucho espacio en blanco bajo el back flotante). */}
      <div className="max-w-content mx-auto px-5 md:px-8 pt-10 md:pt-10 pb-10">
        {/* Back solo en DESKTOP: en mobile lo pone el header flotante del layout (como los otros modos). */}
        <button
          onClick={() => navigate('/modos')}
          aria-label="Volver a modos"
          className="hidden md:inline-flex items-center justify-center w-9 h-9 rounded-full bg-surface-card border border-black/[0.06] shadow-sm hover:bg-neutral-100 transition-colors mb-4"
        >
          <ArrowLeft className="w-5 h-5 text-brand-black/70" />
        </button>
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-brand-primary" />
          <h1 className="text-[24px] md:text-[28px] font-semibold text-brand-black/90">Shorts Studio</h1>
        </div>
        <p className="text-[14px] text-neutral-500 mt-1.5 mb-7 max-w-lg">
          Subí tu video hablando a cámara y elegí un estilo. Lo transformamos por completo con ese look.
        </p>

        <div className="md:flex md:gap-8 md:items-start">
          {/* Panel de generar — solo DESKTOP (en mobile va en el bottom sheet, no se monta acá). */}
          {!isMobile && (
            <aside className="md:w-[380px] md:shrink-0 md:sticky md:top-6">
              {contenido}
              <button
                onClick={generar}
                disabled={!puedeGenerar}
                className="mt-3 w-full h-11 inline-flex items-center justify-center gap-2 rounded-lg bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-40 disabled:cursor-not-allowed text-white text-[15px] font-medium shadow-sm transition-colors"
              >
                {generando ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Generando…
                  </>
                ) : (
                  <>
                    Generar {cost > 0 && <CreditBadge credits={cost} tone="on-primary" size="sm" />}
                  </>
                )}
              </button>
              {!template && (
                <p className="text-[12px] text-neutral-400 mt-2 text-center">Elegí un estilo de la derecha →</p>
              )}
            </aside>
          )}

          {/* Área derecha: normalmente la grilla de plantillas; al generar, SOLO el resultado ocupa el
              LUGAR de la 1ª card (mismo tamaño), con "Cambiar plantilla" DEBAJO (no desnivela). El
              px/-mx da aire para que el borde de las cards no lo corte el overflow del scroll. */}
          <div className="flex-1 min-w-0 md:max-h-[calc(100vh-9.5rem)] md:overflow-y-auto md:px-1 md:-mx-1">
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 pb-1">
              {mostrarResultado ? (
                <div>
                  {resultadoCard}
                  <button
                    onClick={() => setVerPlantillas(true)}
                    className="inline-flex items-center gap-1.5 mt-2.5 text-[13px] font-medium text-brand-primary hover:opacity-80"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" /> Cambiar plantilla
                  </button>
                </div>
              ) : templatesVisibles.map((t) => {
                const activo = templateId === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => seleccionarPlantilla(t.id)}
                    className={`group relative aspect-[3/4] rounded-2xl overflow-hidden text-left transition-all ${
                      activo
                        ? 'ring-2 ring-brand-primary'
                        : 'ring-1 ring-black/[0.06] hover:ring-brand-primary/40 hover:-translate-y-0.5'
                    }`}
                  >
                    {t.image ? (
                      <img src={t.image} alt={t.nombre} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                    ) : (
                      <div className={`absolute inset-0 bg-gradient-to-br ${t.gradient}`} />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                    <div className="absolute bottom-0 inset-x-0 p-3">
                      <p className="text-white font-semibold text-[15px] leading-tight">{t.nombre}</p>
                      <p className="text-white/85 text-[12px]">{t.descripcion}</p>
                    </div>
                    {activo && (
                      <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-brand-primary flex items-center justify-center shadow">
                        <Check className="w-3.5 h-3.5 text-white" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <input ref={fileInputRef} type="file" accept="video/*" className="hidden" onChange={onFile} />

      {/* MOBILE: al tocar una plantilla, sube el sheet desde abajo con subir-video + generar. */}
      {isMobile && (
        <OptionSheet
          open={sheetOpen}
          onClose={() => setSheetOpen(false)}
          title={template ? template.nombre : 'Tu short'}
          hideAccept
        >
          {template && (
            <p className="text-[12.5px] text-neutral-500 mb-3">
              {template.descripcion} · subí tu video y generá.
            </p>
          )}
          {contenido}
        </OptionSheet>
      )}

      {/* Pantalla completa del resultado (estilo Higgsfield): video centrado + botoncitos al costado
          (descargar / cerrar). Click en el fondo también cierra. */}
      {fullscreen && resultUrl && (
        <div
          className="fixed inset-0 z-[1000] bg-brand-black/90 flex items-center justify-center p-4 sm:p-6"
          onClick={() => setFullscreen(false)}
        >
          <div className="flex items-stretch gap-3" onClick={(e) => e.stopPropagation()}>
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <video
              src={resultUrl}
              controls
              autoPlay
              playsInline
              controlsList="nodownload noplaybackrate noremoteplayback"
              disablePictureInPicture
              className="h-[85vh] w-auto max-w-[calc(100vw-7rem)] rounded-2xl bg-black object-contain"
            />
            <div className="flex flex-col gap-2.5">
              <button
                onClick={descargar}
                disabled={descargando}
                aria-label="Descargar"
                className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors disabled:opacity-60"
              >
                {descargando ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
              </button>
              <button
                onClick={() => setFullscreen(false)}
                aria-label="Cerrar"
                className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
